import { useCallback } from 'react';
import type { VINRecord } from '@web/vin-sessions/types';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';
import { googleAnalyticsService } from '@web/services/singletons';
import { useVinSessionState, useVinSessionDispatch } from '@web/vin-sessions/context/vin-sessions.hooks';
import { vinSessionActions } from '@web/vin-sessions/context/vin-sessions.state';
import { buildVinSessionEventProperties, calculateDurationInMilliseconds } from '../helpers';

export type VinSessionEndReason = 'Close Button' | 'Change Vin' | 'Navigate Away';

/**
 * Tracks the GA4 analytics events for a VIN session itself: when it starts, ends, or is abandoned.
 *
 * These three lifecycle events are excluded from the VIN session context source, because the vehicle is their subject
 * rather than ambient context and they fire at session teardown, so they pass the vehicle params explicitly here.
 *
 * @returns the tracking callbacks for the VIN session start, end, and abandon events
 */
export function useVinSessionAnalytics(): {
    trackVinSessionStartEvent: (vinRecord: VINRecord) => void;
    trackVinSessionEndEvent: (endReason: VinSessionEndReason) => void;
    trackVinSessionAbandonEvent: () => void;
} {
    const { selectedVin, sessionLifecycle } = useVinSessionState();
    const dispatch = useVinSessionDispatch();

    const trackVinSessionStartEvent = useCallback((vinRecord: VINRecord) => {
        if (!vinRecord) {
            return;
        }

        const vinSessionEventProperties = buildVinSessionEventProperties(vinRecord);

        googleAnalyticsService.track(GoogleAnalyticsEvents.VinSessionStart, vinSessionEventProperties);
    }, []);

    const trackVinSessionEndEvent = useCallback(
        (endReason: VinSessionEndReason) => {
            if (!selectedVin || !sessionLifecycle || sessionLifecycle.hasEnded) {
                return;
            }
            dispatch(vinSessionActions.setSessionEnded());

            const durationMilliseconds = calculateDurationInMilliseconds(sessionLifecycle.startedAt);
            const vinSessionEventProperties = buildVinSessionEventProperties(selectedVin.vinRecord);

            googleAnalyticsService.track(GoogleAnalyticsEvents.VinSessionEnd, {
                ...vinSessionEventProperties,
                duration_ms: durationMilliseconds,
                end_reason: endReason,
            });
        },
        [selectedVin, sessionLifecycle, dispatch]
    );

    const trackVinSessionAbandonEvent = useCallback(() => {
        if (!selectedVin || !sessionLifecycle || sessionLifecycle.hasEnded) {
            return;
        }
        dispatch(vinSessionActions.setSessionEnded());

        const durationMilliseconds = calculateDurationInMilliseconds(sessionLifecycle.startedAt);
        const vinSessionEventProperties = buildVinSessionEventProperties(selectedVin.vinRecord);

        googleAnalyticsService.track(GoogleAnalyticsEvents.VinSessionAbandon, {
            ...vinSessionEventProperties,
            duration_ms: durationMilliseconds,
        });
    }, [selectedVin, sessionLifecycle, dispatch]);

    return { trackVinSessionStartEvent, trackVinSessionEndEvent, trackVinSessionAbandonEvent };
}
